/**
 * ChatMessageParts — Sub-components for enhanced message rendering.
 * - StreamingDots: animated indicator while AI is responding
 * - ActionChip: styled badge for ---ACTION: tags
 * - ArtifactRenderer: sandboxed preview for html/jsx/tsx/svg code blocks
 *
 * Rule 94: Modüler — sub-components ayrı dosyada.
 */
import { Code2, Copy } from 'lucide-react'
import { useState } from 'react'
import { ACTION_CHIP_COLORS, type CodeArtifact, type ParsedAction } from './ChatMessageUtils'

/* ── Streaming Indicator (animated dots) ── */
export function StreamingDots() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginLeft: 4 }}>
      {[0, 1, 2].map(i => (
        <span
          key={i}
          style={{
            width: 4, height: 4, borderRadius: '50%',
            background: 'var(--os-accent)',
            display: 'inline-block',
            animation: 'sidechat-dot-pulse 1.2s ease-in-out infinite',
            animationDelay: `${i * 0.2}s`,
          }}
        />
      ))}
      <style>{`
        @keyframes sidechat-dot-pulse {
          0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>
    </span>
  )
}

/* ── Action Chip ── */
export function ActionChip({ type, params }: ParsedAction) {
  const colors = ACTION_CHIP_COLORS[type] ?? ACTION_CHIP_COLORS.DEFAULT
  const label = params.length > 0 ? `${type}: ${params[0]}` : type

  return (
    <span
      title={`Action: ${type}${params.length ? ' | ' + params.join(' | ') : ''}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 7px', borderRadius: 20,
        background: colors.bg, color: colors.text,
        border: `1px solid ${colors.border}`,
        fontSize: 9.5, fontWeight: 600, letterSpacing: '0.02em',
        fontFamily: 'inherit', whiteSpace: 'nowrap',
        cursor: 'default', userSelect: 'none',
      }}
    >
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: colors.text, flexShrink: 0 }} />
      {label}
    </span>
  )
}

/* ── Code Artifact Renderer ── */
export function ArtifactRenderer({ artifact }: { artifact: CodeArtifact }) {
  const [expanded, setExpanded] = useState(false)
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(artifact.code)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const isRenderable = artifact.lang === 'html' || artifact.lang === 'svg'

  const iframeSrc = artifact.lang === 'svg'
    ? `<!DOCTYPE html><html><body style="margin:0;background:#0a0a0a;display:flex;align-items:center;justify-content:center;min-height:100vh">${artifact.code}</body></html>`
    : `<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{margin:0;background:#0a0a0a;color:#e2e8f0;font-family:inherit}</style></head><body>${artifact.code}</body></html>`

  return (
    <div style={{
      marginTop: 8, borderRadius: 8, overflow: 'hidden',
      border: '1px solid var(--os-border)',
      background: 'var(--os-card)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '5px 10px', borderBottom: '1px solid var(--os-border)',
        background: 'var(--os-surface)',
      }}>
        <Code2 size={11} style={{ color: 'var(--os-accent)', flexShrink: 0 }} />
        <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--os-muted)', flex: 1 }}>
          {artifact.lang.toUpperCase()} Artifact
        </span>
        <button
          onClick={handleCopy}
          title="Copy code"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: copied ? '#10b981' : 'var(--os-muted)',
            padding: '2px 4px', borderRadius: 4, fontSize: 10,
            display: 'flex', alignItems: 'center', gap: 3,
            fontFamily: 'inherit',
          }}
        >
          <Copy size={10} /> {copied ? 'Copied' : 'Copy'}
        </button>
        {isRenderable && (
          <button
            onClick={() => setExpanded(e => !e)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--os-muted)', padding: '2px 4px', borderRadius: 4,
              fontSize: 10, fontFamily: 'inherit',
            }}
          >
            {expanded ? 'Hide' : 'Preview'}
          </button>
        )}
      </div>

      {/* Collapsed: code snippet */}
      {!expanded && (
        <pre style={{
          margin: 0, padding: '8px 10px',
          fontSize: 10, lineHeight: 1.5,
          color: 'var(--os-muted)',
          overflowX: 'auto', maxHeight: 80,
          fontFamily: "'JetBrains Mono', 'Fira Code', Consolas, monospace",
          whiteSpace: 'pre-wrap', wordBreak: 'break-all',
        }}>
          {artifact.code.slice(0, 200)}{artifact.code.length > 200 ? '…' : ''}
        </pre>
      )}

      {/* Expanded: iframe for html/svg */}
      {expanded && isRenderable && (
        <iframe
          srcDoc={iframeSrc}
          sandbox="allow-scripts allow-same-origin"
          style={{
            width: '100%', height: 200, border: 'none',
            display: 'block', background: '#0a0a0a',
          }}
          title={`${artifact.lang} artifact preview`}
        />
      )}

      {/* Expanded: code for jsx/tsx */}
      {expanded && !isRenderable && (
        <pre style={{
          margin: 0, padding: '8px 10px',
          fontSize: 10, lineHeight: 1.5,
          color: 'var(--os-text)',
          overflowX: 'auto', maxHeight: 240,
          fontFamily: "'JetBrains Mono', 'Fira Code', Consolas, monospace",
          whiteSpace: 'pre-wrap', wordBreak: 'break-all',
        }}>
          {artifact.code}
        </pre>
      )}
    </div>
  )
}
